import { CheckCircleRounded } from '@mui/icons-material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { IntegrationType } from 'generated/graphql';
import { FileUploadName } from './FileUploadName';
import { FeedbackIDFieldSVGIcon, FeedbackTextFieldSVGIcon } from './SVGIcons';

interface FileUploadCompletedModalContentProps {
  fileName: string;
  integrationType: IntegrationType;
}

const FILE_UPLOAD_MODAL_CONTENT = [
  {
    icon: (
      <Box width={24} height={24} px={0.25}>
        <FeedbackIDFieldSVGIcon />
      </Box>
    ),
    heading: 'File requires an ID field',
    text: `ID is a unique identifier for a feedback record. If the file doesn't 
    have an ID field, You can generate it in next steps.`,
  },
  {
    icon: <DateRangeIcon />,
    heading: 'File requires a Feedback Date field',
    text: `Feedback Date is when the feedback was shared by the user. If Feedback 
    Date unavailable, we fall back to the current date.`,
  },
  {
    icon: (
      <Box width={24} height={24} px={0.25}>
        <FeedbackTextFieldSVGIcon />
      </Box>
    ),
    heading: `A descriptive name for a Feedback field is critical for accurate summaries 
      and good predictions`,
    text: `The name of a Feedback field help our models understand the context in 
    which feedback was shared by the user. A good descriptive name helps summarise 
    feedback accurately and boost the quality of predictions.`,
  },
];

export const FileUploadCompletedModalContent = ({
  fileName,
  integrationType,
}: FileUploadCompletedModalContentProps) => {
  return (
    <Stack spacing={2.5} mt={1}>
      <Box
        p={1.5}
        border="1px solid"
        borderColor="divider"
        borderRadius={1}
        sx={{ background: (theme) => alpha(theme.palette.text.primary, 0.02) }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleRounded color="success" />
            <Typography>File Uploaded</Typography>
          </Stack>
          <FileUploadName fileName={fileName} />
        </Stack>
      </Box>
      {integrationType === IntegrationType.Feedback && (
        <Box
          border="1px solid"
          borderRadius={2}
          borderColor="divider"
          sx={{
            background: (theme) => alpha(theme.palette.text.primary, 0.02),
          }}
        >
          <Box
            px={2}
            py={1.5}
            sx={{
              background: (theme) => alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <Typography>
              You can now proceed to review the uploaded file
            </Typography>
          </Box>
          <Stack spacing={2} px={2} py={1.5} sx={{ opacity: 0.7 }}>
            <Typography variant="body2">Note:</Typography>
            {FILE_UPLOAD_MODAL_CONTENT.map((content, index) => (
              <Stack key={index} direction="row" spacing={2}>
                {content.icon}
                <Typography variant="body1">
                  {content.heading}
                  <Box mt={0.25}>
                    <Typography variant="body2">{content.text}</Typography>
                  </Box>
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
